import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Skeleton } from '../ui/Skeleton';
import { DropdownMenu } from '../ui/DropdownMenu';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { CampaignAsk, type CampaignAskProps } from './CampaignAsk';
import type { AskThreadSummary } from '../../services/askThreadService';

/** Props for {@link CampaignAskModal}. */
export interface CampaignAskModalProps extends CampaignAskProps {
  /** Whether the focused ask modal is open. */
  open: boolean;
  /** Close the modal (backdrop, Escape, or the close control). */
  onClose: () => void;
  /** Saved conversations for this surface, newest first. */
  threads: AskThreadSummary[];
  /** The currently open conversation, or null for an unsaved new one. */
  activeThreadId: string | null;
  /**
   * The just-created thread whose AI title is still generating. While this
   * matches the active (untitled) thread, the header shows a skeleton instead
   * of a placeholder that would visibly swap to the real title.
   */
  titlePendingThreadId: string | null;
  /** Start a fresh conversation. */
  onNewConversation: () => void;
  /** Switch to a previous conversation. */
  onSwitchConversation: (threadId: string) => void;
  /** Delete (clear) the active conversation for everyone. */
  onDeleteConversation: () => void;
}

/** Vertical three-dots menu trigger. */
const DotsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg aria-hidden="true" className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M10 6a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm0 5.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm0 5.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
  </svg>
);

const PlusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
  </svg>
);

const ChatIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h8M8 8h8m-8 8h4m4 4l-3-3H6a2 2 0 01-2-2V6a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2h-1l0 4z" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const TrashIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

/** Fallback label for a thread that has no derived title yet. */
const UNTITLED = 'Untitled conversation';

/**
 * The focused ask surface: a modal that holds the full chat with hollie, larger
 * and distraction free. A three-dots menu manages the (team-shared) thread:
 * start a new conversation, switch to a previous one, or clear the current one.
 * Clearing is destructive and shared, so it is gated behind a confirm.
 *
 * The conversation list and active-thread state are owned by the parent; this
 * component only renders them and routes the menu actions back up.
 */
export const CampaignAskModal: React.FC<CampaignAskModalProps> = ({
  open,
  onClose,
  threads,
  activeThreadId,
  titlePendingThreadId,
  onNewConversation,
  onSwitchConversation,
  onDeleteConversation,
  ...chat
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const activeTitle = threads.find((t) => t.id === activeThreadId)?.title || null;
  // A fresh thread's AI title is still generating: show a skeleton rather than a
  // placeholder that would visibly swap once the title lands.
  const titleLoading = !!activeThreadId && activeThreadId === titlePendingThreadId && !activeTitle;

  // For a brand-new conversation (nothing selected yet) the chat area would be
  // blank: fill it with a short prompt and the team's recent conversations so
  // it's easy to pick one back up. Hidden once a thread is active.
  const recentThreads = threads.slice(0, 6);
  const emptyState =
    activeThreadId === null ? (
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center">
          <p className="text-base text-muted-foreground">What would you like to know?</p>
        </div>
        {recentThreads.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className="px-2 text-sm font-medium text-muted-foreground">Recent</p>
            {recentThreads.map((thread) => (
              <button
                key={thread.id}
                type="button"
                onClick={() => onSwitchConversation(thread.id)}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-foreground/80 transition-colors hover:bg-surface-hover"
              >
                <ChatIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{thread.title || UNTITLED}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    ) : undefined;

  const items = [
    {
      label: 'New conversation',
      icon: <PlusIcon className="h-4 w-4" />,
      onClick: () => onNewConversation(),
    },
    ...threads.map((thread) => ({
      label: thread.title || UNTITLED,
      icon:
        thread.id === activeThreadId ? (
          <CheckIcon className="h-4 w-4 text-volt" />
        ) : (
          <ChatIcon className="h-4 w-4" />
        ),
      onClick: () => onSwitchConversation(thread.id),
      disabled: thread.id === activeThreadId,
    })),
    ...(activeThreadId
      ? [
          {
            label: 'Delete conversation',
            icon: <TrashIcon className="h-4 w-4" />,
            className: 'text-danger',
            onClick: () => setConfirmOpen(true),
          },
        ]
      : []),
  ];

  return (
    <Modal open={open} onClose={onClose} maxWidth="max-w-2xl" heightClass="h-[80vh]">
      {/* Fixed-height column: header pinned, the chat fills and scrolls within,
          so the modal holds one size no matter how the conversation grows. */}
      <div className="flex h-full flex-col gap-4">
        <div className="flex shrink-0 items-center justify-between">
          <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
            {titleLoading ? (
              <span data-testid="ask-title-loading" className="inline-block w-40 align-middle">
                <Skeleton height={14} />
              </span>
            ) : (
              activeTitle || 'Ask hollie'
            )}
          </h3>
          <div className="flex items-center gap-1">
            <DropdownMenu
              trigger={
                <button
                  type="button"
                  aria-label="Conversation options"
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground"
                >
                  <DotsIcon className="h-5 w-5" />
                </button>
              }
              items={items}
              align="right"
              width="w-64"
            />
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <CampaignAsk {...chat} autoFocus={open} fill emptyState={emptyState} />
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          onDeleteConversation();
        }}
        title="Delete conversation?"
        description="This clears the conversation for everyone on your team. This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
      />
    </Modal>
  );
};
